import { Module } from 'vuex';

import { buybackService } from '@/services/buybacks/buybacks.service';
import { Buyback, TabCount, UrgentCount } from '@/services/buybacks/buybacks.types';
import { RootState } from '@/store';
import { formsService } from '@/services/forms/forms.service';

export interface BuybackState {
  buyback: Buyback;
  buybackLoading: boolean;
  buybackLabelLoading: boolean;
  urgentCount: UrgentCount;
  tabCounts: TabCount;
}

const state: BuybackState = {
  buyback: null,
  buybackLoading: false,
  buybackLabelLoading: false,
  urgentCount: { reception: 0, deballage: 0, controle: 0, achat: 0, canceled_controle: 0, canceled_expe: 0 },
  tabCounts: {
    validated_to_reflow: 0,
    to_acquire: 0,
    offer_nc_validated: 0,
    suspended_to_suspend: 0,
    suspended_supplier_contacted: 0,
    suspended_pending_verification: 0,
    suspended_contact_supplier: 0,
    canceled_to_ship: 0,
    canceled_to_send: 0,
    canceled_to_refund: 0,
    return_supplier: 0
  }
};

const options: Module<BuybackState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setBuyback: ({ commit, dispatch }, buyback: Buyback | string | number): Promise<void> => {
      if (typeof buyback === 'number' || typeof buyback === 'string') return dispatch('getBuybackById', buyback);
      else {
        commit('buyback', buyback);
      }
    },
    getBuybackById: ({ commit, dispatch }, id: number): Promise<void> => {
      commit('buybackLoading', true);

      return buybackService
        .get(id)
        .then(buyback => buyback && commit('buyback', buyback))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    clearBuyback: ({ dispatch }): Promise<void> => {
      return dispatch('setBuyback', null);
    },
    printBuybackLabel: ({ commit, dispatch, state }): Promise<void> => {
      commit('buybackLabelLoading', true);
      return buybackService
        .printLabel(state.buyback.id)
        .then(zpl => zpl && dispatch('printer/printWith', { data: [zpl], printer: 'CONTROL' }, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLabelLoading', false));
    },
    printReturnLabel: ({ commit, dispatch, state }): Promise<void> => {
      commit('buybackLabelLoading', true);
      const updateShip = !state.buyback.device_id
        ? buybackService.update(state.buyback.id, { ship: true }).then(buyback => commit('buyback', buyback))
        : Promise.resolve();
      return buybackService
        .getReturnLabel(state.buyback.id)
        .then(label => {
          // PDF returned when Shyppi is unavailable: print it like an invoice, on the default printer
          if (label?.pdf_url) {
            return dispatch(
              'printer/printWith',
              { data: [{ type: 'pdf', format: 'file', data: label.pdf_url }] },
              { root: true }
            );
          }
          return (
            label?.zpl && dispatch('printer/printWith', { data: [label.zpl], printer: 'SHIPPING' }, { root: true })
          );
        })
        .then(() => updateShip)
        .then(() => dispatch('alert/pushSuccess', 'BL de retour imprime !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLabelLoading', false));
    },
    declareBuybackReception: (
      { commit, dispatch },
      {
        buybackId,
        box,
        charger,
        other_item,
        other_item_description,
        labelCount
      }: {
        buybackId: number;
        box: boolean;
        charger: boolean;
        other_item: number;
        other_item_description: string | null;
        labelCount: number;
      }
    ): Promise<void> => {
      commit('buybackLabelLoading', true);
      return buybackService
        .update(buybackId, {
          box,
          charger,
          other_item,
          other_item_description,
          is_unpacked: true
        })
        .then(buyback => {
          commit('buyback', buyback);
          if (labelCount > 0) {
            return buybackService.printLabel(buybackId).then(zpl => {
              if (zpl) {
                const labels = Array(labelCount).fill(zpl);
                return dispatch('printer/printWith', { data: labels, printer: 'CONTROL' }, { root: true });
              }
            });
          }
        })
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLabelLoading', false));
    },
    validateBuyback: ({ commit, dispatch, state }, buybackId?: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .validateBuyback(buybackId || state.buyback.id)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Buyback validé !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    counterOfferBuyback: (
      { commit, dispatch },
      { mpBuybackId, ...payload }: { mpBuybackId: string; price: number; reasons: string[]; comment?: string }
    ): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .counterOfferBuyback(mpBuybackId, payload)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Contre-offre déclarée !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    suspendBuyback: (
      { commit, dispatch },
      { mpBuybackId, reasons }: { mpBuybackId: string; reasons: string[] }
    ): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .suspendBuyback(mpBuybackId, reasons)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Buyback suspendu !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    updateBuyback: ({ commit, dispatch, state }, data: Partial<Buyback>): Promise<void> => {
      const buybackId = data.id || state.buyback?.id;
      commit('buybackLoading', true);
      return buybackService
        .update(buybackId, data)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Buyback mis à jour !', { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    generateRmaFile: (
      { commit, dispatch },
      { buybackId, force = false }: { buybackId: number; force?: boolean }
    ): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .generateRmaFile(buybackId, force)
        .then(buyback => {
          commit('buyback', buyback);
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    getBuybackByDeviceId: ({ commit, dispatch }, deviceId: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .getByDeviceId(deviceId)
        .then(buyback => buyback && commit('buyback', buyback))
        .catch(error => {
          if (error?.response?.status === 404) {
            commit('buyback', null);
            return;
          }
          dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => commit('buybackLoading', false));
    },
    getUrgentCount: ({ commit, dispatch }): Promise<void> => {
      return buybackService
        .getUrgentCount()
        .then(count => commit('urgentCount', count))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    getTabCounts: ({ commit, dispatch }): Promise<void> => {
      return buybackService
        .getTabCounts()
        .then(counts => counts && commit('tabCounts', counts))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    updateSuspendedStep: ({ commit, dispatch, state }, step: string): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .updateSuspendedStep(state.buyback.id, step)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    cancelBuyback: (
      { commit, dispatch },
      payload: { buybackId: number; serialNumber?: string; cancellationFile?: File }
    ): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .cancelBuyback(payload.buybackId, payload.serialNumber, payload.cancellationFile)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Buyback annulé !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    deleteBuyback: ({ commit, dispatch }, buybackId: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .delete(buybackId)
        .then(() => dispatch('alert/pushSuccess', 'Buyback supprimé !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    handToShipping: ({ commit, dispatch }, buybackId: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .handToShipping(buybackId)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', "Ordinateur transféré à l'expédition !", { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    declareComputerReception: ({ commit, dispatch }, buybackId: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .declareComputerReception(buybackId)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', "Réception de l'ordinateur enregistrée !", { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    markShipped: (
      { commit, dispatch },
      { buybackId, manualTrackingNumber }: { buybackId: number; manualTrackingNumber: string }
    ): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .markShipped(buybackId, manualTrackingNumber)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Colis marqué comme expédié !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    acquireBuyback: ({ commit, dispatch, state }): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .acquireBuyback(state.buyback.id)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Buyback acquis !', { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    declareSupplierReturn: ({ commit, dispatch, state }): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .declareSupplierReturn(state.buyback.id)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Retour fournisseur déclaré !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    supplierResponse: ({ commit, dispatch, state }): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .supplierResponse(state.buyback.id)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'Fournisseur contacté !', { root: true }))
        .then(() => dispatch('modal/hideModal', null, { root: true }))
        .then(() => dispatch('table/listRows', null, { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    getByTrackingNumber: ({ commit, dispatch }, trackingNumber: string): Promise<any> => {
      commit('buybackLoading', true);
      return buybackService
        .getByTrackingNumber(trackingNumber)
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          return null;
        })
        .finally(() => commit('buybackLoading', false));
    },
    declareReception: (
      { commit, dispatch },
      { buybackId, reservation }: { buybackId: number; reservation?: boolean }
    ): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .declareReception(buybackId, reservation)
        .then(buyback => commit('buyback', buyback))
        .then(() => dispatch('alert/pushSuccess', 'R\u00e9ception d\u00e9clar\u00e9e !', { root: true }))
        .then(() => dispatch('getUrgentCount'))
        .then(() => dispatch('getTabCounts'))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    addReservePhoto: ({ commit, dispatch, state }, photo: File): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .addReservePhoto(state.buyback.id, photo)
        .then(buyback => commit('buyback', buyback))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    deleteReservePhoto: ({ commit, dispatch, state }, photoId: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .deleteReservePhoto(state.buyback.id, photoId)
        .then(buyback => commit('buyback', buyback))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    addSuspensionPhoto: ({ commit, dispatch, state }, photo: File): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .addSuspensionPhoto(state.buyback.id, photo)
        .then(buyback => commit('buyback', buyback))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    },
    deleteSuspensionPhoto: ({ commit, dispatch, state }, photoId: number): Promise<void> => {
      commit('buybackLoading', true);
      return buybackService
        .deleteSuspensionPhoto(state.buyback.id, photoId)
        .then(buyback => commit('buyback', buyback))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('buybackLoading', false));
    }
  },
  mutations: {
    buyback: (state, buyback: Buyback) => (state.buyback = buyback),
    buybackLoading: (state, loading: boolean) => (state.buybackLoading = loading),
    buybackLabelLoading: (state, loading: boolean) => (state.buybackLabelLoading = loading),
    urgentCount: (state, count: UrgentCount) => (state.urgentCount = count),
    tabCounts: (state, counts: TabCount) => (state.tabCounts = counts)
  },
  getters: {
    buybackForm: (state, getters, rootState) =>
      formsService.getForm('buyback', state.buyback, rootState['global-settings'].settings)
  }
};

export default options;
